import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Stack, useTheme } from "@mui/material";
import { Button, LinearProgress, Typography } from "@foodpilot/foods";
import {
    ScoreRecalculationEntity,
    useScoreRecalculation,
} from "src/api/client-api/scoreRecalculation/useScoreRecalculation";
import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";

export type ScoreRecalculationEntityKind = "product" | "ingredient";

type ScoreRecalculationModalProps = {
    path: string;
    pageEntities: ScoreRecalculationEntity[];
    campaignId: number;
    organizationId: number;
    invalidatedQueryKey: string;
    entityKind: ScoreRecalculationEntityKind;
    onClose: () => void;
};

export const ScoreRecalculationModal = (props: ScoreRecalculationModalProps) => {
    const { path, pageEntities, campaignId, organizationId, invalidatedQueryKey, entityKind, onClose } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [pageEntitiesAtOpening] = useState(pageEntities);
    const recalculation = useScoreRecalculation({
        path,
        pageEntities: pageEntitiesAtOpening,
        campaignId,
        organizationId,
        invalidatedQueryKey,
    });
    const { state, loadTargets, questionnaireCount, entityCount, lockedCount, doneCount } = recalculation;
    const isRunning = state.phase === "running";

    useEffect(() => {
        void loadTargets();
    }, [loadTargets]);

    const closeButton = (
        <Button variant="ternary" onClick={onClose}>
            {t("grid.score_recalculation.close", "Close")}
        </Button>
    );

    const lockedLine = lockedCount > 0 && (
        <Typography variant="body" sx={{ color: theme.custom.grey[2000] }}>
            {t("grid.score_recalculation.locked_count", {
                value: lockedCount,
                defaultValue: "Validated questionnaires skipped: {{value}}",
            })}
        </Typography>
    );

    const renderContent = (): { body: ReactNode; actions: ReactNode } => {
        switch (state.phase) {
            case "loading":
                return { body: <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />, actions: null };
            case "load-failed":
                return {
                    body: (
                        <RemoteErrorAlert
                            status="server-error"
                            message={t(
                                "grid.score_recalculation.load_error",
                                "The questionnaires to recalculate could not be loaded.",
                            )}
                            onRetry={loadTargets}
                        />
                    ),
                    actions: closeButton,
                };
            case "confirm":
                if (questionnaireCount === 0) {
                    return {
                        body: (
                            <Stack gap={theme.spacing(1)}>
                                <Typography variant="body">
                                    {t(
                                        "grid.score_recalculation.nothing_to_recalculate",
                                        "No questionnaire to recalculate on this page.",
                                    )}
                                </Typography>
                                {lockedLine}
                            </Stack>
                        ),
                        actions: closeButton,
                    };
                }

                return {
                    body: (
                        <Stack gap={theme.spacing(1)}>
                            <Typography variant="body">
                                {t("grid.score_recalculation.questionnaire_count", {
                                    value: questionnaireCount,
                                    defaultValue:
                                        "Questionnaires to recalculate on this page for the active campaign: {{value}}",
                                })}
                            </Typography>
                            <Typography variant="body">
                                {entityKind === "product" ?
                                    t("grid.score_recalculation.product_count", {
                                        value: entityCount,
                                        defaultValue: "Products covered: {{value}}",
                                    })
                                :   t("grid.score_recalculation.ingredient_count", {
                                        value: entityCount,
                                        defaultValue: "Ingredients covered: {{value}}",
                                    })
                                }
                            </Typography>
                            {lockedLine}
                        </Stack>
                    ),
                    actions: (
                        <>
                            <Button variant="ternary" onClick={onClose}>
                                {t("grid.score_recalculation.cancel", "Cancel")}
                            </Button>
                            <Button variant="primary" onClick={() => void recalculation.start()}>
                                {t("grid.score_recalculation.start", "Recalculate")}
                            </Button>
                        </>
                    ),
                };
            case "running":
                return {
                    body: (
                        <Stack gap={theme.spacing(1)}>
                            <LinearProgress
                                aria-label={t("grid.score_recalculation.title", "Recalculate scores")}
                                value={Math.round((doneCount / questionnaireCount) * 100)}
                            />
                            <Typography variant="body" role="status">
                                {t("grid.score_recalculation.progress", {
                                    done: doneCount,
                                    total: questionnaireCount,
                                    defaultValue:
                                        "{{done}} of {{total}} questionnaires recalculated. Keep this page open.",
                                })}
                            </Typography>
                        </Stack>
                    ),
                    actions: null,
                };
            case "failed":
                return {
                    body: (
                        <RemoteErrorAlert
                            status="server-error"
                            message={
                                state.failedEntityName === null ?
                                    t(
                                        "grid.score_recalculation.interrupted",
                                        "The recalculation was interrupted. Questionnaires already recalculated are kept.",
                                    )
                                :   t("grid.score_recalculation.failed", {
                                        name: state.failedEntityName,
                                        defaultValue:
                                            "The calculation of {{name}} failed. Questionnaires already recalculated are kept.",
                                    })
                            }
                        />
                    ),
                    actions: (
                        <>
                            {closeButton}
                            <Button variant="primary" onClick={() => void recalculation.resume()}>
                                {t("grid.score_recalculation.resume", "Resume")}
                            </Button>
                        </>
                    ),
                };
            case "done":
                return {
                    body: (
                        <Typography variant="body" role="status">
                            {t("grid.score_recalculation.done", {
                                value: doneCount,
                                defaultValue: "Questionnaires recalculated: {{value}}",
                            })}
                        </Typography>
                    ),
                    actions: closeButton,
                };
        }
    };

    const { body, actions } = renderContent();

    return (
        <FoodsBottomSheetDialog
            open
            setOpen={() => undefined}
            onClose={isRunning ? undefined : onClose}
            disableEscapeKeyDown={isRunning}
            aria-labelledby="score-recalculation-dialog-title"
            title={
                <Typography id="score-recalculation-dialog-title" component="h2" variant="big-semibold">
                    {t("grid.score_recalculation.title", "Recalculate scores")}
                </Typography>
            }
            content={
                <Stack gap={theme.spacing(3)} sx={{ p: theme.spacing(3) }}>
                    {body}
                    {actions !== null && (
                        <Stack direction="row" justifyContent="flex-end" gap={theme.spacing(1)}>
                            {actions}
                        </Stack>
                    )}
                </Stack>
            }
        />
    );
};
